<template>
  <v-card class="app-chat position-relative overflow-hidden text-sm">
    <div
      class="chat-content-area h-full d-flex flex-column"
      style="height: 550px"
    >
      <chat-header
        :solicitante-nome="solicitanteNome"
        :solicitante-email="solicitanteEmail"
      />

      <v-divider />

      <chat-log
        :mensagens="mensagens"
        :solicitante-nome="solicitanteNome"
        :pode-responder-mensagem="podeResponderMensagem"
        :ticket-esta-finalizado="ticketEstaFinalizado"
        @mensagem-selecionada-para-resposta="handleSelectMensagem"
      />

      <div
        autocomplete="off"
        class="mt-2 pb-0"
      >
        <chat-resposta-atendente-anexos
          v-if="mensagemAtendente.anexos.length"
          :mostrar-mensagem-de-envio-dos-anexos="mostrarMensagemDeEnvioDosAnexos"
          :loading="loading"
          :lista-de-anexos="mensagemAtendente.anexos"
          :remover-anexo="removerAnexo"
        />

        <chat-mensagem-selecionada-para-resposta
          v-if="mensagemSelecionadaParaResposta"
          :mensagem-selecionada-para-resposta="mensagemSelecionadaParaResposta"
          :handle-cancelar-resposta-mensagem="handleCancelarRespostaMensagem"
        />

        <chat-input
          :ticket-id="ticketId"
          :template-whats-app24-horas-props="templateWhatsApp24HorasProps"
          :chat-input-message.sync="mensagemAtendente.resposta"
          :handle-upload-anexos="handleUploadAnexos"
          :handle-send-message-click="handleSendMessageClick"
          :editavel="editavel"
          :loading="loading"
          :apagar-audio="apagarAudio"
          :pode-gravar-audio="podeGravarAudio"
          :pode-responder-mensagem="podeResponderMensagem"
          :ticket-esta-finalizado="ticketEstaFinalizado"
          @update:audio-data-url="v => mensagemAtendente.audioDataUrl = v"
        />
      </div>
    </div>
  </v-card>
</template>

<script>
import Vue from 'vue'
import { ref, watchEffect } from '@vue/composition-api'
import { mdiClose, mdiMenu } from '@mdi/js'
import ChatHeader from './ChatHeader.vue'
import ChatLog from './ChatLog.vue'
import ChatRespostaAtendenteAnexos from './ChatRespostaAtendenteAnexos.vue'
import ChatMensagemSelecionadaParaResposta from './ChatMensagemSelecionadaParaResposta.vue'
import ChatInput from './ChatInput.vue'
import TicketService from '@/services/modules/TicketService'
import { appendFilesToList, findFilesInFileList, removeFileFromList } from '@/utils/files'
import { EventBus } from '@/utils/EventBus'

export default {
  emits: ['refetch-data'],

  components: {
    ChatHeader,
    ChatLog,
    ChatRespostaAtendenteAnexos,
    ChatMensagemSelecionadaParaResposta,
    ChatInput,
  },

  props: {
    ticketId: {
      type: Number,
      default: 0,
    },
    solicitanteNome: {
      type: String,
      required: true,
    },
    solicitanteEmail: {
      type: String,
      default: null,
    },
    mensagens: {
      type: Array,
      default: () => [],
    },
    templateWhatsApp24HorasProps: {
      type: Object,
      required: true,
    },
    editavel: {
      type: Boolean,
      required: true,
    },
    podeGravarAudio: {
      type: Boolean,
      required: true,
    },
    podeResponderMensagem: {
      type: Boolean,
      required: true,
    },
    ticketEstaFinalizado: {
      type: Boolean,
      required: true,
    },
  },

  setup(props, { emit }) {
    const mensagemAtendente = ref({
      ticketId: '',
      resposta: '',
      anexos: [],
      mensagemReferenciaId: null,
      audioDataUrl: null,
    })
    const mensagemSelecionadaParaResposta = ref(null)
    const loading = ref(false)
    const apagarAudio = ref(false)
    const mostrarMensagemDeEnvioDosAnexos = ref(false)

    watchEffect(() => {
      if (!mostrarMensagemDeEnvioDosAnexos.value) return
      setTimeout(() => { mostrarMensagemDeEnvioDosAnexos.value = false }, 2000)
    })

    watchEffect(() => {
      if (!apagarAudio.value) return
      setTimeout(() => { apagarAudio.value = false }, 2000)
    })

    function removerAnexo(anexo) {
      mensagemAtendente.value.anexos = removeFileFromList(mensagemAtendente.value.anexos, anexo)
    }

    function handleSelectMensagem(mensagemId) {
      mensagemSelecionadaParaResposta.value = props.mensagens.find(it => it.id === mensagemId)
    }

    function handleCancelarRespostaMensagem() {
      mensagemSelecionadaParaResposta.value = null
    }

    async function handleUploadAnexos(event) {
      let { files: anexosNovos } = event.target
      anexosNovos = Array.from(anexosNovos)

      if (findFilesInFileList(mensagemAtendente.value.anexos, anexosNovos)) {
        Vue.$toast.warning('Não são permitidos anexos com os mesmos nomes.')
        return
      }
      if (mensagemAtendente.value.anexos.length + anexosNovos.length > 6) {
        Vue.$toast.warning('Máximo de 6 anexos')
        return
      }
      if (anexosNovos.some(file => file.size / 1_048_576 > 5)) {
        Vue.$toast.warning('O anexo não pode ter mais de 5MB.', { timeout: false })
        return
      }

      const anexos = await appendFilesToList(mensagemAtendente.value.anexos, anexosNovos)
      mensagemAtendente.value.anexos = anexos.map(it => ({ nome: it.nome, dataUrl: it.dataUrl }))
    }

    async function handleSendMessageClick() {
      const naoGravouAudio = !mensagemAtendente.value.audioDataUrl
      const podeEscreverMensagemMasNaoEscreveu = !props.templateWhatsApp24HorasProps.notificarObrigatoriedadeDoEnvioDoTemplateDe24Horas && !mensagemAtendente.value.resposta

      if (loading.value) return
      if (naoGravouAudio) {
        if (podeEscreverMensagemMasNaoEscreveu) return
      }

      loading.value = true
      mensagemAtendente.value.ticketId = props.ticketId
      mensagemAtendente.value.mensagemReferenciaId = mensagemSelecionadaParaResposta.value?.id || null

      try {
        await TicketService.enviarMensagemAgente(props.ticketId, mensagemAtendente.value)

        mostrarMensagemDeEnvioDosAnexos.value = !!mensagemAtendente.value.anexos.length
        mensagemAtendente.value.resposta = ''
        mensagemAtendente.value.audioDataUrl = null
        mensagemAtendente.value.anexos = []
        mensagemSelecionadaParaResposta.value = null
        apagarAudio.value = true

        emit('refetch-data')
        EventBus.$emit('refetch-ticket-slas')
      } finally {
        loading.value = false
      }
    }

    return {
      mensagemAtendente,
      mensagemSelecionadaParaResposta,
      loading,
      apagarAudio,
      mostrarMensagemDeEnvioDosAnexos,
      removerAnexo,
      handleSelectMensagem,
      handleCancelarRespostaMensagem,
      handleUploadAnexos,
      handleSendMessageClick,
      icons: {
        mdiMenu,
        mdiClose,
      },
    }
  },
}
</script>
